// llmAgentRunner — Glue mellan runPipeline (agent-orchestrering) och en
// abstract LLM-call. Detta är AgentRunner-implementationen som produktion
// använder: för varje agent paketerar vi systemPrompt + allowedTools +
// previousOutput och skickar till LLM:en.
//
// Tester injicerar en deterministisk `llmCall`-mock. Produktion injicerar
// en wrapper runt ai-workflow-assistant edge-function. Hooken
// useDiagramPipeline (X2) sätter ihop denna med React-state.

import type { Agent, AgentInput, AgentOutput, AgentRunner } from '@/lib/aiAgents/agentRegistry';
import type { ToolDefinition } from '@/lib/aiToolRegistry';
import { filterToolsForAgent } from '@/lib/aiAgents/agentRegistry';
import { EXTENDED_TOOLS } from '@/lib/aiToolWrappersExtended';
import { AI_TOOLS } from '@/lib/aiToolRegistry';

export interface LlmAgentCallInput {
  /** Agenten som ska köras. */
  agent: Agent;
  /** User-prompten (oförändrad genom pipeline). */
  userPrompt: string;
  /** Föregående agents output, om någon. */
  previousOutput: unknown | null;
  /** Tool-definitioner som agenten FÅR kalla (filtrerade per allowedTools). */
  allowedTools: ToolDefinition[];
  /** Språk för output-strängar. */
  language?: 'sv' | 'en';
}

export interface LlmAgentCallResult {
  /** Tool-agnostisk strukturerad payload som nästa agent kan läsa som previousOutput. */
  payload: unknown;
  /** Faktisk credit-kostnad (inte estimate). Returneras av tokenMeter. */
  cost: number;
  /** Tool-calls agenten gjorde (för audit + stepper-summary). */
  toolCalls: Array<{ tool: string; args: Record<string, unknown> }>;
  /** Om agenten failade — ok:false. */
  ok: boolean;
  /** Fel-meddelande om ok:false. */
  error?: string;
}

/**
 * Kontrakt: inject:a en LlmCall för att koppla runner till valfri backend
 * (edge-function, direct-API, mock). Se tester för mock-signatur.
 */
export type LlmCall = (input: LlmAgentCallInput) => Promise<LlmAgentCallResult>;

/**
 * Paketera tool-definitioner som agenten får använda. Mergas base + extended
 * för kompletthet. Filtrera sedan mot agent.allowedTools.
 */
export function resolveAgentTools(agent: Agent): ToolDefinition[] {
  const all = [...AI_TOOLS, ...EXTENDED_TOOLS];
  return filterToolsForAgent(agent, all);
}

/**
 * Skapa en AgentRunner från en LlmCall. Runner:n är vad runPipeline
 * tar emot och kallar per agent. Injecta mock för tests; produktion
 * använder createEdgeFunctionLlmCall.
 */
export function createLlmAgentRunner(llmCall: LlmCall): AgentRunner {
  return async function llmAgentRunner(agent: Agent, input: AgentInput): Promise<AgentOutput> {
    const allowedTools = resolveAgentTools(agent);
    try {
      const res = await llmCall({
        agent,
        userPrompt: input.sharedContext.userPrompt,
        previousOutput: input.previousOutput,
        allowedTools,
        language: input.sharedContext.language,
      });
      return {
        agent: agent.id,
        ok: res.ok,
        payload: res.payload,
        cost: res.cost,
        toolCalls: res.toolCalls,
        error: res.error,
      };
    } catch (e) {
      return {
        agent: agent.id,
        ok: false,
        payload: null,
        cost: 0,
        toolCalls: [],
        error: (e as Error).message ?? 'llm-call-threw',
      };
    }
  };
}

// ── Mock LlmCall för tester + dev ──────────────────────────────
export interface MockAgentResponse {
  payload?: unknown;
  cost?: number;
  toolCalls?: Array<{ tool: string; args: Record<string, unknown> }>;
  ok?: boolean;
  error?: string;
}

/**
 * Skapa en LlmCall som returnerar canned responses per agent-id. Används
 * främst i tester + lokal dev utan live LLM. Saknade agents → ok:false.
 */
export function createMockLlmCall(
  responses: Partial<Record<Agent['id'], MockAgentResponse>>,
): LlmCall {
  return async (input: LlmAgentCallInput): Promise<LlmAgentCallResult> => {
    const r = responses[input.agent.id];
    if (!r) {
      return {
        ok: false,
        payload: null,
        cost: 0,
        toolCalls: [],
        error: `no-mock-response-for: ${input.agent.id}`,
      };
    }
    return {
      ok: r.ok ?? true,
      payload: r.payload ?? null,
      cost: r.cost ?? 10,
      toolCalls: r.toolCalls ?? [],
      error: r.error,
    };
  };
}
